import { Star } from 'lucide-react';
import { ProjectSelector } from '../../ProjectSelector';
import { CostCentreSelector } from '../../CostCentreSelector';
import { RoleSelector } from '../../RoleSelector';
import { BookingHoverCard } from '../../ScheduledBookings';
import type { StatusDotType } from '../../ScheduledBookings/StatusDot';
import { OvertimeTypeCell } from '../OvertimeTypeCell';
import type { CostCentre, TimesheetEntry } from '@/types/timesheet';
import type { OrganisationTimesheetSettings, OvertimeType } from '@/types/timesheetSettings';
import type { Project } from '@/types';

export interface RowSelectorCellsProps {
  entry: TimesheetEntry;
  index: number;
  /** The row's resolved project, if it is in the loaded set. */
  project: Project | undefined;
  allAvailableProjects: Project[];
  /** Cost centres for the row's project, enriched with favourite/recent metadata. */
  enrichedCostCentres: CostCentre[];
  isReadOnly: boolean;
  isAbsence: boolean;
  isScheduled: boolean;
  statusType: StatusDotType;
  isSearchingProjects: boolean;
  companyId: string | undefined;
  screenMode: 'live' | 'demo';
  overtimeEnabled: boolean;
  activeOvertimeTypes: OvertimeType[];
  organizationSettings: OrganisationTimesheetSettings | undefined;
  onUpdateEntry: (index: number, field: keyof TimesheetEntry, value: unknown) => void;
  onProjectSearch: (query: string) => void;
  onFetchCostCentres: (projectCode: string) => void;
  onCostCentreUsed: (costCentreId: string) => void;
}

/** Project, Cost Centre, Role and Work Type cells for a single timesheet row. */
export function RowSelectorCells({
  entry,
  index,
  project,
  allAvailableProjects,
  enrichedCostCentres,
  isReadOnly,
  isAbsence,
  isScheduled,
  statusType,
  isSearchingProjects,
  companyId,
  screenMode,
  overtimeEnabled,
  activeOvertimeTypes,
  organizationSettings,
  onUpdateEntry,
  onProjectSearch,
  onFetchCostCentres,
  onCostCentreUsed,
}: RowSelectorCellsProps) {
  const isFavouriteProject = allAvailableProjects.find(p => p.id === entry.projectId)?.isFavourite;

  const handleProjectChange = (projectId: string) => {
    onUpdateEntry(index, 'projectId', projectId);
    // Clear cost centre when project changes - user must select a new one
    onUpdateEntry(index, 'costCentreId', '');
    // Fetch filtered cost centres for the new project
    const proj = allAvailableProjects.find(p => p.id === projectId);
    if (proj?.code) {
      onFetchCostCentres(proj.code);
    }
  };

  const editableProjectSelector = (
    <ProjectSelector
      projects={allAvailableProjects}
      value={entry.projectId}
      onChange={handleProjectChange}
      disabled={isReadOnly}
      required={true}
      isSubmitted={isReadOnly}
      // Server-side search props
      onServerSearch={onProjectSearch}
      isSearching={isSearchingProjects}
      companyId={companyId}
      fallbackName={entry.projectName}
      fallbackCode={entry.projectCode}
      projectSelectionScope={organizationSettings?.projectSelectionScope}
    />
  );

  return (
    <>
      <td className="p-1 sm:p-1.5">
        {/* Show project selector for absence entries */}
        {isAbsence ? (
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-1 min-w-0">
              <ProjectSelector
                projects={allAvailableProjects}
                value={entry.projectId}
                onChange={() => {}} // No-op for absence entries
                disabled={true}
                // Server-side search props
                onServerSearch={onProjectSearch}
                isSearching={isSearchingProjects}
                companyId={companyId}
                projectSelectionScope={organizationSettings?.projectSelectionScope}
                fallbackName={entry.projectName}
                fallbackCode={entry.projectCode}
              />
            </div>
            {isFavouriteProject && (
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
            )}
          </div>
        ) : isScheduled ? (
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-1 min-w-0">
              <BookingHoverCard booking={entry} statusType={statusType}>
                {editableProjectSelector}
              </BookingHoverCard>
            </div>
            {isFavouriteProject && (
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-1 min-w-0">{editableProjectSelector}</div>
            {isFavouriteProject && (
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
            )}
          </div>
        )}
      </td>
      {organizationSettings?.showCostCentre !== false && (
        <td className="p-1 sm:p-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-1 min-w-0">
              <CostCentreSelector
                costCentres={enrichedCostCentres}
                value={entry.costCentreId}
                onChange={(value) => {
                  if (!isAbsence) {
                    onUpdateEntry(index, 'costCentreId', value);
                    onCostCentreUsed(value);
                  }
                }}
                disabled={isAbsence || isReadOnly || (!project && !entry.projectId)}
                fallbackName={entry.costCentreName}
                fallbackCode={entry.costCentreCode}
                required={organizationSettings?.requireCostCentre}
                isSubmitted={isReadOnly}
                projectSelectionScope={organizationSettings?.projectSelectionScope}
              />
            </div>
            {enrichedCostCentres.find(c => c.id === entry.costCentreId)?.isFavourite && (
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
            )}
          </div>
        </td>
      )}
      {organizationSettings?.showRole && (
        <td className="p-1 sm:p-1.5">
          <RoleSelector
            value={entry.roleId}
            onChange={(roleId, roleName) => {
              if (!isAbsence) {
                onUpdateEntry(index, 'roleId', roleId);
                onUpdateEntry(index, 'roleName', roleName);
              }
            }}
            disabled={isAbsence || isReadOnly}
            required={organizationSettings?.requireRole}
            screenMode={screenMode}
            displayName={entry.roleName}
            isSubmitted={isReadOnly}
          />
        </td>
      )}
      {/* Work Type cell */}
      {overtimeEnabled && (
        <td className="p-1 sm:p-1.5">
          <OvertimeTypeCell
            entry={entry}
            overtimeTypes={activeOvertimeTypes}
            isReadOnly={isReadOnly}
            isAbsence={isAbsence}
            onChangeOvertimeType={(typeId) => {
              const ot = activeOvertimeTypes.find(t => t.id === typeId);
              onUpdateEntry(index, 'overtimeTypeId', typeId);
              onUpdateEntry(index, 'overtimeTypeName', ot?.name);
              onUpdateEntry(index, 'overtimeCostMultiplier', ot?.costMultiplier);
              onUpdateEntry(index, 'overtimeBillMultiplier', ot?.billMultiplier);
            }}
          />
        </td>
      )}
    </>
  );
}
